import React from 'react';
import { useSelector } from 'react-redux';
import { ModalFooter, Text, VStack } from '@chakra-ui/react';

import StackedButtonGroup from '../components/Modal/ModalButtonGroups/StackedButtonGroup';
import ProcoreWritebackWarning from '../components/ProcoreWritebackWarning';
import { selectModalButtons, showModal } from '../redux/modules/modal';

import { showDontAskAgainModal } from './askAgainModalUtil';
import {
  dismissRecordWarning,
  fetchDepartmentProcoreWriteback,
  loadProcoreWarningContext,
  shouldWarnAboutProcore
} from './procoreWritebackUtil';

/**
 * Tracks the record-level "don't show again" box between the modal, which owns
 * the checkbox, and the handler, which acts on confirm. The choice is only
 * saved if the user goes ahead -- ticking it and cancelling saves nothing.
 */
export const createRecordDismissal = (context, scope) => {
  const state = { checked: false };

  return {
    onDismissChange: scope === 'record' ? checked => { state.checked = checked; } : undefined,
    commit: () => {
      if (state.checked) dismissRecordWarning(context);
    }
  };
};

/**
 * A confirmation carrying the Procore warning.
 *
 * Always shown, even when the user previously ticked "Don't ask me again" on
 * the plain archive confirmation: that box predates Procore writing, and
 * honouring it here would let a payment-affecting action through unwarned.
 */
export const showProcoreConfirmModal = ({
  dispatch,
  title,
  description,
  scope,
  affectedRecords = null,
  context,
  label = 'Confirm',
  onConfirm
}) => {
  const dismissal = createRecordDismissal(context, scope);

  dispatch(showModal(
    () => {
      const { primaryButtonOptions } = useSelector(selectModalButtons) || {};

      return (
        <>
          <VStack alignItems="stretch" pt={2} spacing={4}>
            {description && <Text fontSize="md">{description}</Text>}
            <ProcoreWritebackWarning
              affectedRecords={affectedRecords}
              scope={scope}
              onDismissChange={dismissal.onDismissChange}
            />
          </VStack>
          <ModalFooter>
            <StackedButtonGroup
              isLoading={primaryButtonOptions?.isLoading}
              label={label}
              onClick={() => {
                dismissal.commit();
                onConfirm();
              }}
            />
          </ModalFooter>
        </>
      );
    },
    { size: 'md', title }
  ));
};

/**
 * Procore-aware replacement for showDontAskAgainModal on archive actions.
 *
 * Warns when the company writes to Procore (and, for records, this user has
 * not dismissed it); otherwise behaves exactly as before via `fallback`, which
 * defaults to the existing don't-ask-again confirmation.
 */
export const confirmArchiveWithProcoreWarning = async ({
  dispatch,
  scope,
  cookieId,
  description,
  onClick,
  title,
  label,
  fallback
}) => {
  const context = await dispatch(loadProcoreWarningContext());

  if (!shouldWarnAboutProcore(context, scope)) {
    if (fallback) {
      fallback();
    } else {
      showDontAskAgainModal({ dispatch, cookieId, description, onClick, title });
    }

    return;
  }

  showProcoreConfirmModal({ dispatch, title, description, scope, context, label, onConfirm: onClick });
};

/**
 * Department archive: as above, but the warning states how many records lose
 * their Procore rows, read for this department specifically.
 */
export const confirmDepartmentArchiveWithProcoreWarning = async ({
  dispatch,
  departmentId,
  cookieId,
  description,
  onClick,
  title
}) => {
  const status = await fetchDepartmentProcoreWriteback(departmentId);

  // A department with nothing in Procore has nothing to warn about.
  if (!status.enabled || status.affectedRecords === 0) {
    showDontAskAgainModal({ dispatch, cookieId, description, onClick, title });
    return;
  }

  showProcoreConfirmModal({
    dispatch,
    title,
    description,
    scope: 'department',
    affectedRecords: status.affectedRecords,
    context: status,
    onConfirm: onClick
  });
};

/**
 * For delete confirmations built on ConfirmActionWarningModal: the
 * `procoreWarning` prop to pass (null when no warning is due) and a `commit`
 * to call when the user confirms.
 */
export const loadProcoreDeleteWarning = async (dispatch, scope) => {
  const context = await dispatch(loadProcoreWarningContext());

  if (!shouldWarnAboutProcore(context, scope)) {
    return { procoreWarning: null, commit: () => {} };
  }

  const dismissal = createRecordDismissal(context, scope);

  return {
    procoreWarning: { scope, onDismissChange: dismissal.onDismissChange },
    commit: dismissal.commit
  };
};
